import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  SensitiveDataAccessEvent,
  SensitiveDataAccessService,
  SensitiveDataAccessSortDirection,
} from '../../../services/sensitive-data-access/sensitive-data-access.service';

@Component({
    selector: 'app-sensitive-data-access',
    templateUrl: './sensitive-data-access.component.html',
    styleUrls: ['./sensitive-data-access.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SensitiveDataAccessComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'createdAt',
    'actorId',
    'businessPurposeCode',
    'referenceId',
    'action',
    'resourceId',
    'eventType',
    'rowCount',
  ];
  readonly pageSize = 50;

  events: SensitiveDataAccessEvent[] = [];
  searchValue = '';
  sortBy = 'createdAt';
  sortDirection: SensitiveDataAccessSortDirection = 'desc';
  loading = false;
  exporting = false;
  endOfList = false;
  loadError = false;

  private offset = 0;
  private requestGeneration = 0;
  private readonly searchChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;

  constructor(
    private sensitiveDataAccessService: SensitiveDataAccessService,
    private snackBar: MatSnackBar,
  ) {
    this.searchSubscription = this.searchChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => void this.resetAndLoad());
  }

  ngOnInit(): void {
    void this.resetAndLoad();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
    this.requestGeneration += 1;
  }

  queueSearch(value: string): void {
    this.searchChanges.next(value || '');
  }

  clearSearch(): void {
    this.searchValue = '';
    this.searchChanges.next('');
  }

  changeSort(sort: Sort): void {
    this.sortBy = sort.active || 'createdAt';
    this.sortDirection = sort.direction === 'asc' ? 'asc' : 'desc';
    void this.resetAndLoad();
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 150) {
      void this.loadEvents();
    }
  }

  retry(): void {
    void this.loadEvents();
  }

  formatValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return `${value}`.replace(/_/g, ' ');
  }

  formatContextValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return 'Not provided';
    }
    return this.formatValue(value);
  }

  getActorName(event: SensitiveDataAccessEvent): string {
    const name = [event.actorFirstName, event.actorLastName].filter(Boolean).join(' ').trim();
    return name || event.actorEmail || event.actorId;
  }

  getActorDetail(event: SensitiveDataAccessEvent): string {
    if (event.actorEmail && event.actorEmail !== this.getActorName(event)) {
      return event.actorEmail;
    }
    return event.actorType === 'system' ? 'System' : event.actorId;
  }

  getResourceName(event: SensitiveDataAccessEvent): string {
    if (event.resourceName) {
      return event.resourceName;
    }
    if (event.resourceId === 'draft') {
      return 'Draft report';
    }
    return this.formatValue(event.resourceId);
  }

  getResourceDetail(event: SensitiveDataAccessEvent): string {
    if (event.resourceId === 'draft' && event.resourceName) {
      return 'Draft';
    }
    return this.formatValue(event.resourceType);
  }

  async exportEvents(): Promise<void> {
    if (this.exporting) {
      return;
    }
    this.exporting = true;
    try {
      const events: SensitiveDataAccessEvent[] = [];
      const limit = 200;
      let offset = 0;
      while (true) {
        const page = await this.sensitiveDataAccessService.listEvents({
          limit,
          offset,
          search: this.searchValue.trim(),
          sortBy: this.sortBy,
          sortDirection: this.sortDirection,
        });
        events.push(...page);
        if (page.length < limit) {
          break;
        }
        offset += page.length;
      }
      this.downloadCsv(events);
    } catch (error) {
      console.error('Unable to export sensitive data access history', error);
      this.snackBar.open('Unable to export sensitive data access history.', 'Close', { duration: 4000 });
    } finally {
      this.exporting = false;
    }
  }

  private downloadCsv(events: SensitiveDataAccessEvent[]): void {
    const headers = [
      'Accessed',
      'Actor name',
      'Actor email or ID',
      'Business purpose',
      'Reference type',
      'Reference ID',
      'Action',
      'Resource name',
      'Resource state or type',
      'Resource ID',
      'Outcome',
      'Outcome code',
      'Subjects',
      'Rows',
    ];
    const rows = events.map(event => [
      event.createdAt,
      this.getActorName(event),
      this.getActorDetail(event),
      this.formatContextValue(event.businessPurposeCode),
      this.formatContextValue(event.referenceType),
      this.formatContextValue(event.referenceId),
      this.formatValue(event.action),
      this.getResourceName(event),
      this.getResourceDetail(event),
      this.formatValue(event.resourceId),
      this.formatValue(event.eventType),
      this.formatValue(event.outcomeCode),
      event.subjectCount,
      event.rowCount === null || event.rowCount === undefined ? '' : event.rowCount,
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(value => this.escapeCsvValue(value)).join(','))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `sensitive-data-access-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private escapeCsvValue(value: string | number | null | undefined): string {
    const text = value === null || value === undefined ? '' : `${value}`;
    return `"${text.replace(/"/g, '""')}"`;
  }

  private async resetAndLoad(): Promise<void> {
    this.requestGeneration += 1;
    this.events = [];
    this.offset = 0;
    this.endOfList = false;
    this.loadError = false;
    this.loading = false;
    await this.loadEvents();
  }

  private async loadEvents(): Promise<void> {
    if (this.loading || this.endOfList) {
      return;
    }

    const generation = this.requestGeneration;
    this.loading = true;
    this.loadError = false;
    try {
      const events = await this.sensitiveDataAccessService.listEvents({
        limit: this.pageSize,
        offset: this.offset,
        search: this.searchValue.trim(),
        sortBy: this.sortBy,
        sortDirection: this.sortDirection,
      });
      if (generation !== this.requestGeneration) {
        return;
      }
      this.events = this.events.concat(events);
      this.offset += events.length;
      this.endOfList = events.length < this.pageSize;
    } catch (error) {
      if (generation !== this.requestGeneration) {
        return;
      }
      console.error('Unable to load sensitive data access history', error);
      this.loadError = true;
      this.snackBar.open('Unable to load sensitive data access history.', 'Close', { duration: 4000 });
    } finally {
      if (generation === this.requestGeneration) {
        this.loading = false;
      }
    }
  }
}
